import bookingConfig from '@/config/Booking'

/**
 * Travel Period Composable
 * Manages travel date selection and validation across components
 * Optimized for production with SSR compatibility
 */
export const useTravelPeriod = (options = {}) => {
  const {
    enableExtendedDays = false,
    enableHotelParamsUpdate = false,
    autoNavigate = true,
    skipInitialization = false,
    componentName = 'unknown',
    customValidation = null
  } = options

  // Core composables
  const packageStore = usePackageStore()
  const bookStore = useBookStore()
  const dayjs = useDayjs()
  const { t } = useI18n()

  // State
  const showDatePicker = ref(false)
  const selectedDates = ref([])
  const warningMessage = ref('')

  // Custom dates store integration
  const customDatesStore = useCustomDatesStore?.() // Optional for SSR

  // Initialize dates based on priority
  const initializeDates = () => {
    if (skipInitialization) return

    const packageItem = packageStore.package
    if (!packageItem) return

    let startDate, endDate

    // Priority: custom dates > booking dates > package dates
    if (customDatesStore?.hasCustomDates && customDatesStore.isActiveFor(componentName)) {
      startDate = customDatesStore.customStartDate
      endDate = customDatesStore.customEndDate
    } else if (bookStore.startDate && bookStore.endDate) {
      startDate = bookStore.startDate
      endDate = bookStore.endDate
    } else {
      startDate = packageItem.startDate
      endDate = packageItem.endDate
    }

    if (startDate && endDate) {
      selectedDates.value = [startDate, endDate]
    }
  }

    // Computed properties
  const packageItem = computed(() => packageStore.package)

  // Cached valid event dates for performance
  const validEventDates = computed(() => {
    const events = packageItem.value?.events
    if (!events?.length) return []

    return events
      .filter(event => event?.date)
      .map(event => dayjs(event.date))
      .filter(date => date.isValid())
  })

  const isSelectedRangeCoversEvents = computed(() => {
    if (!selectedDates.value?.length || !validEventDates.value.length) return true

    const [startDate, endDate] = selectedDates.value
    if (!startDate || !endDate) return true

    try {
      const start = dayjs(startDate)
      const end = dayjs(endDate)

      if (!start.isValid() || !end.isValid()) return true

      return validEventDates.value.every(eventDate =>
        eventDate.isSameOrAfter(start) && eventDate.isSameOrBefore(end)
      )
    } catch {
      return true
    }
  })

  const currentStartDate = computed(() =>
    (customDatesStore?.hasCustomDates && customDatesStore.isActiveFor(componentName))
      ? customDatesStore.customStartDate
      : selectedDates.value?.[0] || bookStore.startDate || packageItem.value?.startDate
  )

  const currentEndDate = computed(() =>
    (customDatesStore?.hasCustomDates && customDatesStore.isActiveFor(componentName))
      ? customDatesStore.customEndDate
      : selectedDates.value?.[1] || bookStore.endDate || packageItem.value?.endDate
  )

  const isShowingPackageDate = computed(() => {
    const pkg = packageItem.value
    return pkg && currentStartDate.value === pkg.startDate && currentEndDate.value === pkg.endDate
  })

  const allowedDateRange = computed(() => {
    const eventDates = validEventDates.value
    if (!eventDates.length) return []

    // Efficient min/max calculation using timestamps
    const timestamps = eventDates.map(date => date.valueOf())
    const minTime = Math.min(...timestamps)
    const maxTime = Math.max(...timestamps)

    const earliestDate = dayjs(minTime)
    const latestDate = dayjs(maxTime)

    return [
      earliestDate.format('YYYY-MM-DD'),
      enableExtendedDays
        ? latestDate.add(60, 'day').format('YYYY-MM-DD')
        : latestDate.format('YYYY-MM-DD')
    ]
  })

    const highlightedDates = computed(() =>
    validEventDates.value.map(date => date.format('YYYY-MM-DD'))
  )

  // Methods
  const toggleDatePicker = () => {
    showDatePicker.value = !showDatePicker.value
  }

  const validateDateRange = (startDate, endDate) => {
    if (!validEventDates.value.length || !startDate || !endDate) {
      return { isValid: true }
    }

    try {
      const selectedStart = dayjs(startDate)
      const selectedEnd = dayjs(endDate)

      if (!selectedStart.isValid() || !selectedEnd.isValid()) {
        return { isValid: false, message: 'Invalid date format' }
      }

      if (selectedStart.isAfter(selectedEnd)) {
        return { isValid: false, message: 'Start date must be before end date' }
      }

      const allEventsCovered = validEventDates.value.every(eventDate =>
        eventDate.isSameOrAfter(selectedStart) && eventDate.isSameOrBefore(selectedEnd)
      )

      if (customValidation) {
        const customResult = customValidation(startDate, endDate, packageItem.value)
        if (!customResult.isValid) return customResult
      }

      warningMessage.value = allEventsCovered
        ? ''
        : t('trip.conditions.no_shortened_travel_period')

      return { isValid: true, warning: warningMessage.value }
    } catch {
      return { isValid: false, message: 'Date validation error' }
    }
  }

  const setCustomDates = (dates) => {
    if (!customDatesStore) return

    if (dates?.length === 2 && dates[0] && dates[1]) {
      customDatesStore.setCustomDates(dates[0], dates[1], componentName)
    }
  }

  const clearCustomDates = () => {
    if (customDatesStore) {
      customDatesStore.clearCustomDates(componentName)
    }
  }

  const updateHotelParameters = (startDate, endDate, hotelsSearchParams) => {
    if (!enableHotelParamsUpdate || !hotelsSearchParams?.length) return

    hotelsSearchParams.forEach(param => {
      param.checkin = startDate
      param.checkout = endDate
    })
  }

  const handleTravelPeriod = async (customOptions = {}) => {
    const { hotelsSearchParams, resetWarningsAndErrors } = customOptions

    if (selectedDates.value?.length !== 2) return

    const [startDate, endDate] = selectedDates.value

    const validation = validateDateRange(startDate, endDate)
    if (!validation.isValid) {
      throw new Error(validation.message || 'Invalid date range')
    }

    updateHotelParameters(startDate, endDate, hotelsSearchParams)
    setCustomDates(selectedDates.value)

    // Batch store updates for better performance
    Object.assign(bookStore, {
      startDate,
      endDate,
      ...(enableExtendedDays && {
        tripExtendedDays: calculateExtendedDays(startDate, endDate)
      })
    })

    resetWarningsAndErrors?.()
    return validation
  }

  // Optimized extended days calculation
  const calculateExtendedDays = (startDate, endDate) => {
    const pkg = packageItem.value
    if (!pkg?.startDate || !pkg?.endDate) return 0

    const packageStart = dayjs(pkg.startDate)
    const packageEnd = dayjs(pkg.endDate)
    const selectedStart = dayjs(startDate)
    const selectedEnd = dayjs(endDate)

    const daysBefore = Math.max(0, packageStart.diff(selectedStart, 'day'))
    const daysAfter = Math.max(0, selectedEnd.diff(packageEnd, 'day'))

    return daysBefore + daysAfter
  }

      // Optimized watcher setup
  let bookStoreWatcher = null

  if (customDatesStore) {
    bookStoreWatcher = watch(
      () => [bookStore.startDate, bookStore.endDate],
      ([newStartDate, newEndDate]) => {
        if (!customDatesStore.isActiveFor(componentName) || !newStartDate || !newEndDate) return

        const currentCustomDates = customDatesStore.getCustomDates
        if (currentCustomDates?.[0] !== newStartDate || currentCustomDates?.[1] !== newEndDate) {
          customDatesStore.setCustomDates(newStartDate, newEndDate, componentName)
          selectedDates.value = [newStartDate, newEndDate]
        }
      },
      { immediate: false }
    )
  }

  // Safe lifecycle hook registration
  try {
    if (typeof onMounted === 'function') {
      onMounted(() => {
        if (!customDatesStore?.preventOverride(componentName)) {
          initializeDates()
        }
      })
    } else {
      initializeDates()
    }
  } catch {
    initializeDates()
  }

  // Safe cleanup registration
  if (bookStoreWatcher) {
    try {
      if (typeof onUnmounted === 'function') {
        onUnmounted(() => {
          bookStoreWatcher?.()
          bookStoreWatcher = null
        })
      }
    } catch {
      // Cleanup will happen on next component mount
    }
  }

  return {
    // State
    showDatePicker,
    selectedDates,
    warningMessage,

    // Computed
    isSelectedRangeCoversEvents,
    currentStartDate,
    currentEndDate,
    isShowingPackageDate,
    allowedDateRange,
    highlightedDates,

    // Methods
    toggleDatePicker,
    validateDateRange,
    handleTravelPeriod,
    setCustomDates,
    clearCustomDates
  }
}
